import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ShippingScheduleService } from 'src/app/services/shipping-schedule/shipping-schedule.service';
import { PortService } from 'src/app/services/port/port.service';
import { RegionService } from 'src/app/services/region/region.service';
import { PaginationInfo } from 'src/app/shared/models/api-response.interface';

@Component({
  selector: 'app-ss-listing',
  templateUrl: './ss-listing.component.html',
  styleUrls: ['./ss-listing.component.scss']
})
export class SsListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  shippingSchedules: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Port data for dynamic columns
  portFromList: any[] = [];
  destinationPortList: any[] = [];
  isLoadingPorts: boolean = false;

  // Region data
  regions: any[] = [];
  isLoadingRegions: boolean = false;
  selectedRegion: string = '';

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    search: '',
    date_from: '',
    date_to: '',
    region: ''
  };

  constructor(
    private _shippingScheduleService: ShippingScheduleService,
    private _portService: PortService,
    private _regionService: RegionService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadRegions();
    this.loadPortData();
    this.loadShippingSchedules();
  }

  ngAfterViewInit(): void {
    this.refreshMenus();
  }

  private loadRegions(): void {
    this.isLoadingRegions = true;
    this.subscriptions.push(
      this._regionService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          this.isLoadingRegions = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            const regionData = response.data.data || response.data || [];
            this.regions = regionData.map((region: any) => ({
              slug: region.slug,
              name: region.name
            }));
          } else {
            console.error('Invalid region data response:', response);
            this.regions = [];
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoadingRegions = false;
          console.error('Error loading regions:', error);
          this.regions = [];
          this.cdr.detectChanges();
        }
      })
    );
  }

  private loadPortData(): void {
    this.isLoadingPorts = true;
    this.subscriptions.push(
      this._portService.ShipScheduleList().subscribe({
        next: (response: any) => {
          this.isLoadingPorts = false;
          if (response.status && response.data) {
            this.portFromList = response.data.port_from || [];
            this.destinationPortList = response.data.destination_port || [];
          } else {
            console.error('Invalid port data response:', response);
            this.portFromList = [];
            this.destinationPortList = [];
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoadingPorts = false;
          console.error('Error loading port data:', error);
          this.portFromList = [];
          this.destinationPortList = [];
          this.cdr.detectChanges();
        }
      })
    );
  }

  private loadShippingSchedules(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;

    this.subscriptions.push(
      this._shippingScheduleService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.shippingSchedules = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.shippingSchedules = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading shipping schedules:', error);
          this.shippingSchedules = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadShippingSchedules(page, this.searchTerm, this.filters);
  }

  onSearch(): void {
    this.filters.search = this.searchTerm;
    this.loadShippingSchedules(1, this.searchTerm, this.filters);
  }

  onApplyFilters(): void {
    this.loadShippingSchedules(1, this.searchTerm, this.filters);
    this.showFiltersForm = false;
  }

  onResetFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: '',
      region: ''
    };
    this.searchTerm = '';
    this.selectedRegion = '';
    this.loadShippingSchedules(1, '', {});
    this.showFiltersForm = false;
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  // Get port value for a specific shipping schedule and port
  getPortValue(schedule: any, portId: string, portType: 'from' | 'destination'): string {
    if (!schedule) return '-';

    const portDataKey = portType === 'from' ? 'port_from_data' : 'destination_port_data';
    const portData = schedule[portDataKey];

    if (!portData || !Array.isArray(portData)) return '-';

    const portEntry = portData.find((p: any) => p.port_id === portId);
    return portEntry ? portEntry.value : '-';
  }

  // Edit action
  onEdit(schedule: any): void {
    this.router.navigate(['/shipping-and-docs/shipping-schedule/edit', schedule._id]);
  }

  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  private refreshMenus(): void {
    // Refresh any dropdown menus after data load
    setTimeout(() => {
      if (typeof (window as any).KTMenu !== 'undefined') {
        (window as any).KTMenu.createInstances();
      }
    }, 100);
  }

  // Region filter
  onRegionClick(regionSlug: string): void {
    // Toggle region selection
    if (this.selectedRegion === regionSlug) {
      this.selectedRegion = '';
      this.filters.region = '';
    } else {
      this.selectedRegion = regionSlug;
      this.filters.region = regionSlug;
    }
    // Reload data with region filter
    this.loadShippingSchedules(1, this.searchTerm, this.filters);
  }

  // Navigation
  navigateToAdd(): void {
    this.router.navigate(['/shipping-and-docs/shipping-schedule/add']);
  }
}
